import { z } from "zod"
import { OrderPreview } from "@/features/dashboard/order-preview"
import { PanelErrorBoundary } from "@/components/panel-error-boundary"
import { DashboardSkeleton } from "@/features/dashboard/dashboard-skeleton"
import {
	useDashboardActive,
	DASHBOARD_REFRESH_MS,
} from "@/features/dashboard/use-dashboard-polling"
import { formatDateTime } from "@/lib/utils"
import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { DashboardOverviewSchema } from "@optimist/schemas"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { DashboardOverviewContent } from "@/features/dashboard/dashboard-overview"
import { api } from "@/lib/api"
import { queryKeys } from "@/lib/query-keys"
import { usePortalAuth } from "@/features/authz/hooks/use-portal-auth"

export const Route = createFileRoute("/dashboard/")({
	validateSearch: (search): { previewOrder?: string } => {
		const result = z.uuid().safeParse(search.previewOrder)
		return result.success ? { previewOrder: result.data } : {}
	},
	component: DashboardRoute,
})

function DashboardRoute() {
	const { previewOrder } = Route.useSearch()
	const navigate = Route.useNavigate()
	const openPreview = (orderId: string) => {
		void navigate({ search: { previewOrder: orderId }, viewTransition: false, resetScroll: false })
	}
	return (
		<PanelErrorBoundary title="Dashboard overview">
			<DashboardPage onPreview={openPreview} />
			<OrderPreview orderId={previewOrder ?? null} onClose={() => {
				void navigate({ search: {}, replace: true, viewTransition: false, resetScroll: false })
			}} />
		</PanelErrorBoundary>
	)
}

export function DashboardPage({ onPreview }: { onPreview: (orderId: string) => void }) {
	const active = useDashboardActive()
	const [page, setPage] = useState(1)
	const auth = usePortalAuth()
	const canRead =
		auth.data !== undefined && auth.data.permissions.includes("orders.read")
	const dashboard = useQuery({
		queryKey: queryKeys.dashboard(page),
		queryFn: async ({ signal }) =>
			DashboardOverviewSchema.parse(
				await api.get<unknown>("/dashboard", { params: { page }, signal }),
			),
		enabled: canRead && active,
		staleTime: DASHBOARD_REFRESH_MS,
		refetchInterval: active ? DASHBOARD_REFRESH_MS : false,
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: false,
	})
	return (
		<div className="space-y-6">
			<PageHeader
				title="Dashboard overview"
				actions={
					canRead ? (
						<span role="status" className="text-meta text-muted-foreground">
							{dashboard.isFetching
								? "Refreshing…"
								: dashboard.data
									? `Last refreshed ${formatDateTime(dashboard.data.generatedAt)}`
									: ""}
						</span>
					) : undefined
				}
			/>
			{auth.isError ? (
				<div role="alert" className="text-body text-destructive">
					Unable to check dashboard access.{" "}
					<Button variant="outline" onClick={() => void auth.refetch()}>
						Retry
					</Button>
				</div>
			) : auth.isPending ? (
				<DashboardSkeleton />
			) : !canRead ? (
				<p className="text-body text-muted-foreground">
					You do not have permission to view order metrics.
				</p>
			) : dashboard.isError ? (
				<div
					role="alert"
					className="rounded-2xl border border-border bg-card p-6 text-body"
				>
					<p>
						Dashboard metrics could not be loaded. Queue counts are unavailable.
					</p>
					<Button
						className="mt-3"
						variant="outline"
						onClick={() => void dashboard.refetch()}
					>
						Retry
					</Button>
				</div>
			) : dashboard.data ? (
				<DashboardOverviewContent
					data={dashboard.data}
					onPreview={onPreview}
					onPageChange={setPage}
					busy={dashboard.isFetching}
				/>
			) : (
				<DashboardSkeleton />
			)}
		</div>
	)
}
